<template>
  <a-modal
    :title="isEditing ? '编辑关键词' : '添加关键词'"
    v-model:visible="visibleValue"
    :confirm-loading="confirmLoading"
    @cancel="handleCancel"
    :maskClosable="true"
    :keyboard="true"
    :destroyOnClose="true"
    :width="560"
    class="keyword-modal"
    :footer="null"
    :getPopupContainer="getPopupContainer"
  >
    <div class="modal-content">
      <a-form
        :model="formState"
        :rules="rules"
        ref="formRef"
        layout="vertical"
      >
        <a-form-item label="关键词内容" name="content">
          <a-input v-model:value="formState.content" placeholder="请输入关键词" />
        </a-form-item>
        
        <a-form-item label="匹配方式" name="matchType">
          <a-radio-group v-model:value="formState.matchType">
            <a-radio value="EXACT">完全匹配</a-radio>
            <a-radio value="CONTAINS">包含匹配</a-radio>
          </a-radio-group>
        </a-form-item>
        
        <a-form-item label="状态" name="isActive">
          <a-switch v-model:checked="formState.isActive" />
          <span style="margin-left: 8px;">{{ formState.isActive ? '启用' : '禁用' }}</span>
        </a-form-item>
        
        <div class="modal-footer">
          <a-button @click="handleCancel">取消</a-button>
          <a-button type="primary" :loading="confirmLoading" @click="handleSubmit">
            确定
          </a-button>
        </div>
      </a-form>
    </div>
  </a-modal>
</template>

<script>
import { ref, reactive, onMounted, watch, computed } from 'vue';

export default {
  name: 'KeywordRegisterModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    confirmLoading: {
      type: Boolean,
      default: false
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    keyword: {
      type: Object,
      default: () => ({
        _id: null,
        content: '',
        matchType: 'EXACT',
        priority: 1,
        isActive: true
      })
    }
  },
  emits: ['update:visible', 'cancel', 'submit'],
  setup(props, { emit }) {
    console.log('KeywordRegisterModal setup, initial visible:', props.visible);
    
    const formRef = ref(null);
    
    // 폼 상태
    const formState = reactive({
      _id: null,
      content: '',
      matchType: 'EXACT',
      priority: 1,
      isActive: true
    });
    
    // 폼 검증 규칙
    const rules = {
      content: [
        { required: true, message: '请输入关键词内容', trigger: 'blur' },
        { min: 1, max: 50, message: '关键词长度应为1-50个字符', trigger: 'blur' }
      ],
      matchType: [
        { required: true, message: '请选择匹配方式', trigger: 'change' }
      ]
    };
    
    // 양방향 바인딩을 위한 computed 속성
    const visibleValue = computed({
      get: () => props.visible,
      set: (val) => {
        console.log('Setting visible to', val);
        emit('update:visible', val);
      }
    });
    
    // 폼 초기화
    const resetForm = () => {
      formState._id = null;
      formState.content = '';
      formState.matchType = 'EXACT';
      formState.priority = 1;
      formState.isActive = true;
      
      if (formRef.value) {
        formRef.value.resetFields();
      }
    };
    
    // 키워드 데이터가 변경되면 폼 상태 업데이트
    watch(() => props.keyword, (newKeyword) => {
      if (newKeyword) {
        formState._id = newKeyword._id;
        formState.content = newKeyword.content;
        formState.matchType = newKeyword.matchType;
        formState.priority = newKeyword.priority || 1;
        formState.isActive = newKeyword.isActive;
      }
    }, { immediate: true, deep: true });
    
    // prop 변화 감시
    watch(() => props.visible, (newVal) => {
      console.log('visible prop changed to', newVal);
      if (!newVal) {
        resetForm();
      }
    });
    
    // 모달 취소 핸들러
    const handleCancel = () => {
      console.log('Modal cancel button clicked');
      emit('update:visible', false);
      emit('cancel');
    };
    
    // 모달 확인 핸들러
    const handleSubmit = async () => {
      console.log('Modal submit button clicked');
      try {
        await formRef.value.validate();
        
        // 폼 데이터 구성 (isActive는 수정 모드에서만 포함)
        const submitData = {
          _id: formState._id,
          content: formState.content,
          matchType: formState.matchType,
          priority: 1
        };
        
        // 수정 모드일 때만 isActive 포함
        if (props.isEditing) {
          submitData.isActive = formState.isActive;
        }
        
        // 부모 컴포넌트에 제출 이벤트 발생
        emit('submit', submitData);
      } catch (error) {
        // 유효성 검사 오류는 여기서 자동 처리됨
        console.error('Form validation error:', error);
      }
    };
    
    // 모달 컨테이너 설정
    const getPopupContainer = () => {
      return document.body;
    };
    
    // 컴포넌트 마운트 시 체크
    onMounted(() => {
      console.log('KeywordRegisterModal mounted');
    });
    
    return {
      formRef,
      formState,
      rules,
      handleCancel,
      handleSubmit,
      getPopupContainer,
      visibleValue
    };
  }
};
</script>

<style lang="scss" scoped>
.keyword-modal {
  :deep(.ant-modal-content) {
    overflow: hidden;
    border-radius: 8px !important;
    
    .ant-modal-header {
      background-color: #f8f9fa;
      padding: 16px 24px;
      margin-bottom: 0;
      position: relative;
      border-bottom: 1px solid #f0f0f0;
      
      .ant-modal-title {
        font-size: 18px;
        font-weight: 600;
        color: #1f2937;
      }
    }
    
    .ant-modal-body {
      padding: 0;
    }
    
    .ant-modal-close {
      top: 12px;
      right: 12px;
    }
  }
  
  .modal-content {
    border: none;
    padding: 24px;
    position: relative;
    
    .modal-footer {
      margin-top: 24px;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      
      .ant-btn {
        height: 36px;
        font-size: 14px;
        border-radius: 4px;
        padding: 0 16px;
        
        &.ant-btn-primary {
          font-weight: 500;
        }
      }
    }
  }
}

:deep(.ant-form-item-label) {
  padding-bottom: 4px;
  
  label {
    font-weight: 500;
    color: #4b5563;
    font-size: 14px;
    height: 22px;
    line-height: 22px;
  }
  
  // 키워드 내용 라벨은 더 강조
  label[for="content"] {
    font-weight: 600;
  }
}

:deep(.ant-radio-wrapper) {
  margin-right: 16px;
}

:deep(.ant-input), :deep(.ant-input-number), :deep(.ant-input-number-input) {
  border-radius: 4px;
  height: 36px;
  padding: 8px 12px;
  border-color: #d1d5db;
  
  &:hover, &:focus {
    border-color: #3b82f6;
  }
}

:deep(.ant-input-number) {
  width: 100%;
  
  .ant-input-number-handler-wrap {
    border-radius: 0 4px 4px 0;
  }
}

:deep(.ant-form-item) {
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

// 힌트 텍스트 스타일
.form-item-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 6px;
  line-height: 1.5;
}

// 확인 팝업 버튼을 가로로 배치하기 위한 강화된 스타일
:deep(.ant-popconfirm-buttons) {
  display: flex !important;
  flex-direction: row !important;
  justify-content: flex-end !important;
  
  .ant-btn {
    margin-left: 8px !important;
    margin-top: 0 !important;
    
    &:first-child {
      margin-left: 0 !important;
    }
  }
}
</style>
